import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { HomeSolutionsCard, HomeSolutionsTab, HomeSolutionsTabsData, HomeCta } from "@/lib/homepage";

type SolutionsTabsProps = {
  data?: HomeSolutionsTabsData;
};

const TAB_ICON_COLORS: Record<string, string> = {
  industry: '#2AAFB0',
  role: '#9747FF',
  usecase: '#EF974C',
};

const DEFAULT_SOLUTIONS_DATA: HomeSolutionsTabsData = {
  kicker: "Industry",
  heading: "Your business finances on overdrive",
  subheading: "Financial solutions when you need them the most",
  viewMoreCta: { label: "View More", url: "/solutions" },
  tabs: [
    {
      id: "industry",
      label: "Industry",
      description: "Description of tab",
      cards: [
        { title: "Manufacturing", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
        { title: "B2B Commerce", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
        { title: "Financial", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
        { title: "Automotive", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
        { title: "Retail", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
        { title: "Construction", description: "Description of tab fdogjsdlfkhjgsd fgpoiadjksfgkmsdf gsdaofjg", icon: "" },
      ],
    },
    {
      id: "role",
      label: "Your Role",
      description: "Description of tab",
      cards: [
        { title: "CFO & Finance Leaders", description: "Strategic financial management and currency risk oversight solutions", icon: "" },
        { title: "Treasury Managers", description: "Advanced treasury operations and liquidity management tools", icon: "" },
        { title: "Risk Managers", description: "Comprehensive risk assessment and mitigation strategies", icon: "" },
        { title: "Operations Teams", description: "Streamlined operational processes and workflow optimization", icon: "" },
        { title: "Compliance Officers", description: "Regulatory compliance and reporting automation solutions", icon: "" },
        { title: "IT Directors", description: "Technology integration and system architecture support", icon: "" },
      ],
    },
    {
      id: "usecase",
      label: "Your Use Case",
      description: "Description of tab",
      cards: [
        { title: "Emerging Markets", description: "Efficient international payment processing and settlement", icon: "" },
        { title: "System Integrators", description: "Advanced hedging strategies to mitigate FX risk exposure", icon: "" },
        { title: "Business Incubators", description: "Maximize working capital efficiency and liquidity management", icon: "" },
        { title: "Developers", description: "Streamlined reporting across multiple subsidiaries and regions", icon: "" },
        { title: "eCommerce", description: "Letters of credit, trade guarantees and supply chain financing", icon: "" },
        { title: "Professional Services", description: "Automated compliance reporting for multiple jurisdictions", icon: "" },
      ],
    },
  ],
};



function SolutionCard({ title, description, icon, iconColor }: HomeSolutionsCard & { iconColor?: string }) {
  return (
    <a href="#" className="flex grow" >
    <div className="flex flex-col justify-center items-start w-full gap-4 p-6 rounded-xl solutionCard-Outter hover:bg-onplexus-gold-bright/50">
      <div className="solutionCard-Inner w-full">
        <div className="flex items-center gap-4 w-full cardTitle">
          {icon ? (
            <div 
              className="flex items-center justify-center w-8 h-8 p-2 rounded"
              style={{ backgroundColor: iconColor || '#2AAFB0' }}
            >
              <span className="text-white text-4xl onplexus-icon">{icon}</span>
            </div>
          ) : null}
          <h3 className="flex-1 text-xl font-bold text-white leading-6">
            {title}
          </h3>
        </div>
        {description ? (
          <p className="text-sm text-white/70 leading-normal">{description}</p>
        ) : null}
      </div>
    </div>
    </a>
  );
}

function TabButton({ 
  isActive, 
  tab,
  onClick,
}: {
  isActive: boolean;
  tab: HomeSolutionsTab;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 p-px rounded-xl transition-all duration-300 solutionsTab-Nav ${
        isActive 
          ? 'bg-gradient-to-b from-onplexus-gold/60 to-onplexus-gold/28' 
          : 'bg-gradient-to-b from-onplexus-gold/25 to-onplexus-gold-bright/8'
      }`}
    >
      <div
        className="p-6 rounded-[11px] transition-all duration-300"
        style={{
          background: isActive
            ? 'radial-gradient(62.45% 78.18% at 51.48% 100%, rgba(241, 191, 12, 0.34) 0%, rgba(231, 196, 71, 0.05) 100%), #363949'
            : 'linear-gradient(180deg, rgba(231, 196, 71, 0.05) 0%, rgba(231, 196, 71, 0.00) 100%), #363949'
        }}
      >
        <div className="space-y-2 text-left">
          <div className="flex items-center gap-1 flex-wrap">
            <span className={`text-xl font-normal ${
              isActive ? 'text-white' : 'text-white/70'
            }`}>
              Solution by
            </span>
            <span className="text-xl font-bold text-white">
              {tab.label}
            </span>
          </div>
          <p className={`text-base font-normal ${
            isActive ? 'text-white' : 'text-white/70'
          }`}>
            {tab.description}
          </p>
        </div>
      </div>
    </button>
  );
}

function ViewMoreButton({ cta }: { cta?: HomeCta | null }) {
  if (!cta) return null;
  const isExternal = /^(https?:)?\/\//i.test(cta.url) || cta.url.startsWith("mailto:") || cta.url.startsWith("tel:");
  if (isExternal) {
    return (
      <Button asChild className="bg-onplexus-darker text-white hover:text-onplexus-darker hover:bg-onplexus-gold rounded-xl px-8 py-3 h-11 font-normal text-lg">
        <a href={cta.url}>{cta.label}</a>
      </Button>
    );
  }
  return (
    <Button asChild className="bg-onplexus-darker text-white hover:bg-onplexus-darker/80 rounded-xl px-8 py-3 h-11 font-normal text-lg">
      <Link to={cta.url}>{cta.label}</Link>
    </Button>
  );
}

export default function SolutionsTabs({ data }: SolutionsTabsProps) {
  const resolvedTabs = data?.tabs && data.tabs.length > 0 ? data.tabs : DEFAULT_SOLUTIONS_DATA.tabs;
  const resolved: HomeSolutionsTabsData = {
    kicker: data?.kicker || DEFAULT_SOLUTIONS_DATA.kicker,
    heading: data?.heading || DEFAULT_SOLUTIONS_DATA.heading,
    subheading: data?.subheading || DEFAULT_SOLUTIONS_DATA.subheading,
    tabs: resolvedTabs,
    viewMoreCta: data?.viewMoreCta ?? DEFAULT_SOLUTIONS_DATA.viewMoreCta,
  };

  const firstTabId = useMemo(() => resolved.tabs[0]?.id ?? "", [resolved.tabs]);
  const [activeTab, setActiveTab] = useState(firstTabId);

  useEffect(() => {
    setActiveTab(firstTabId);
  }, [firstTabId]);

  const currentTab = useMemo(() => {
    return resolved.tabs.find((tab) => tab.id === activeTab) ?? resolved.tabs[0];
  }, [activeTab, resolved.tabs]);

  if (!resolved.tabs.length || !currentTab) return null;

  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center gap-12">
          {/* Section Header */}
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-3">
              <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
              <span className="title-mini">
                {resolved.kicker}
              </span>
              <div className="h-0.5 w-24 bg-gradient-to-l from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white leading-7">
                {resolved.heading}
              </h2>
              <p className="text-2xl text-white/70 leading-7">
                {resolved.subheading}
              </p>
            </div>
          </div>

          {/* Tabs Container */}
          <div className="solutionsTabs-Outter">
            <div className="w-full max-w-4xl p-2 rounded-3xl solutionsTabs-Inner">
              <div className="p-3 pb-4 rounded-2xl bg-onplexus-dark" style={{
                background: `radial-gradient(100% 100% at 50% 100%, rgba(231, 196, 71, 0.1) 0%, rgba(231, 196, 71, 0.00) 90%), #363949`
              }}>
                
                {/* Tab Navigation */}
                <div className="flex flex-col md:flex-row gap-3 mb-3">
                  {resolved.tabs.map((tab) => (
                    <TabButton
                      key={tab.id}
                      isActive={activeTab === tab.id}
                      tab={tab}
                      onClick={() => setActiveTab(tab.id)}
                    />
                  ))}
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                  {/* Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {currentTab.cards.map((card, index) => (
                      <SolutionCard
                        key={`${currentTab.id}-${card.title ?? index}`}
                        title={card.title}
                        description={card.description}
                        icon={card.icon}
                        iconColor={TAB_ICON_COLORS[currentTab.id]}
                      />
                    ))}
                  </div>

                  {/* View More Button */}
                  <div className="flex justify-center">
                    <ViewMoreButton cta={resolved.viewMoreCta} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
